import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { authFetch } from '../utils/authFetch';
import { getFarmOverviewPath } from '../utils/farmRoutes';
import { isGlobalRole } from '../utils/roles';
import ShefaPageLoadOverlay from '../components/ui/ShefaPageLoadOverlay';

function normalizeControllerType(type) {
  const ty = String(type || '').trim().toLowerCase();
  if (!ty) return 'unknown';
  if (ty === 'water') return 'water';
  if (ty === 'climate' || ty === 'grow') return 'climate';
  return ty;
}

export default function UnassignedDevicesPage() {
  const { t } = useLang();
  const { token, user, ready, logout } = useAuth();
  const { effectiveFarmId } = useFarm();
  const farmOverviewPath = getFarmOverviewPath(effectiveFarmId) || '/';
  const [devices, setDevices] = useState([]);
  const [farms, setFarms] = useState([]);
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowBusy, setRowBusy] = useState({});

  const handleUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  const load = useCallback(async () => {
    if (!token || !isGlobalRole(user?.role)) return;
    setLoading(true);
    setError('');
    try {
      const dRes = await authFetch('/api/admin/devices/unassigned', {}, { token, onUnauthorized: handleUnauthorized });
      if (!dRes.ok) {
        const payload = await dRes.json().catch(() => ({}));
        setError(payload?.error || t('unassignedPage.loadError'));
        return;
      }
      const dJson = await dRes.json();
      const fRes = await authFetch('/api/farms', {}, { token, onUnauthorized: handleUnauthorized });
      const fJson = fRes.ok ? await fRes.json() : [];
      setDevices(Array.isArray(dJson) ? dJson : []);
      setFarms(Array.isArray(fJson) ? fJson : []);
    } catch {
      setError(t('unassignedPage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token, user?.role, handleUnauthorized, t]);

  useEffect(() => {
    if (ready && token && isGlobalRole(user?.role)) load();
  }, [ready, token, user?.role, load]);

  useEffect(() => {
    if (!ready || !token || !isGlobalRole(user?.role)) return undefined;
    const onRefresh = () => {
      load();
    };
    window.addEventListener('shefa-unassigned-refresh', onRefresh);
    return () => window.removeEventListener('shefa-unassigned-refresh', onRefresh);
  }, [ready, token, user?.role, load]);

  if (!ready) {
    return null;
  }

  if (!token) return <Navigate to="/login" replace />;
  if (!isGlobalRole(user?.role)) return <Navigate to="/" replace />;

  const assign = async (dev) => {
    const id = dev._id ? String(dev._id) : dev.deviceId || dev.id;
    const farmId = selection[id];
    if (!farmId) return;
    setRowBusy((b) => ({ ...b, [id]: true }));
    try {
      const res = await authFetch(
        `/api/admin/devices/${encodeURIComponent(id)}/farm`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ farmId }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('unassignedPage.saveError'));
        return;
      }
      setError('');
      await load();
    } catch {
      setError(t('unassignedPage.saveError'));
    } finally {
      setRowBusy((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div className="container py-4">
      <ShefaPageLoadOverlay show={loading} />
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h3 mb-1">{t('unassignedPage.title')}</h1>
          <p className="text-muted mb-0">{t('unassignedPage.subtitle')}</p>
        </div>
        <Link to={farmOverviewPath} className="btn btn-outline-secondary">
          {t('roomsPage.back')}
        </Link>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? null : devices.length === 0 ? (
        <p className="text-muted">{t('unassignedPage.empty')}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>{t('unassignedPage.colDevice')}</th>
                <th>{t('unassignedPage.colFarm')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((dev) => {
                const id = dev._id ? String(dev._id) : dev.deviceId || dev.id;
                const label = dev.displayName || dev.name || dev.deviceId || dev.id || id;
                const ty = normalizeControllerType(dev.type || dev.controller || dev.deviceType);
                const typeMeta =
                  ty === 'water'
                    ? {
                        label: t('adminControllers.controllerTypeWater'),
                        className: 'admin-controller-type-badge admin-controller-type-badge--water',
                        icon: 'fas fa-droplet',
                      }
                    : ty === 'climate'
                    ? {
                        label: t('adminControllers.controllerTypeClimate'),
                        className: 'admin-controller-type-badge admin-controller-type-badge--climate',
                        icon: 'fas fa-wind',
                      }
                    : {
                        label: t('adminControllers.controllerTypeGeneric'),
                        className: 'admin-controller-type-badge admin-controller-type-badge--generic',
                        icon: 'fas fa-microchip',
                      };
                return (
                  <tr key={id}>
                    <td>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <div className="fw-semibold">{label}</div>
                        <span className={typeMeta.className}>
                          <i className={`${typeMeta.icon} me-1`} aria-hidden="true" />
                          {typeMeta.label}
                        </span>
                      </div>
                      <div className="small text-muted font-monospace">{dev.deviceId || dev.id || id}</div>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <select
                        className="form-select form-select-sm"
                        value={selection[id] || ''}
                        onChange={(e) => setSelection((s) => ({ ...s, [id]: e.target.value }))}
                      >
                        <option value="">{t('farmsAdmin.selectFarmNewUser')}</option>
                        {farms.map((f) => (
                          <option key={f._id} value={f._id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!selection[id] || rowBusy[id]}
                        onClick={() => assign(dev)}
                      >
                        {rowBusy[id] ? t('unassignedPage.loading') : t('unassignedPage.apply')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
